import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    VStack,
    HStack,
    Heading,
    Button,
    useDisclosure,
    useColorModeValue,
    Alert,
    AlertIcon,
    Spinner,
    Text,
    Select,
    Input,
    InputGroup,
    InputLeftElement,
    Badge,
    Card,
    CardBody,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Flex,
    Tooltip
} from '@chakra-ui/react';
import {
    MdAdd,
    MdSearch,
    MdFilterList,
    MdRefresh,
    MdVisibility,
    MdVisibilityOff
} from 'react-icons/md';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import EventScheduleModal from '../components/EventScheduling/EventScheduleModal';
import EventDetailsModal from '../components/EventScheduling/EventDetailsModal';
import { ToastManager } from '../components/Common/ToastManager';

const localizer = momentLocalizer(moment);

/**
 * Event Scheduling Page Component with Big Calendar Integration
 */
function EventScheduling({ userProfile, appConfig }) {
    const bgMain = useColorModeValue('gray.50', 'gray.900');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const cardBg = useColorModeValue('white', 'gray.700');
    const headingColor = useColorModeValue('gray.800', 'white');
    const textSecondary = useColorModeValue('gray.600', 'gray.400');
    const textTertiary = useColorModeValue('gray.500', 'gray.400');
    
    // State management
    const [events, setEvents] = useState([]);
    const [allEvents, setAllEvents] = useState([]);
    const [applications, setApplications] = useState([]);
    const [clients, setClients] = useState([]);
    const [statistics, setStatistics] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [calendarView, setCalendarView] = useState('month');
    const [showFilters, setShowFilters] = useState(true);
    const [searchParams] = useSearchParams();
    const [filters, setFilters] = useState({
        applicationId: '',
        clientId: '',
        eventType: '',
        isCompleted: '',
        search: ''
    });
    
    // Modal controls
    const { 
        isOpen: isCreateModalOpen, 
        onOpen: onCreateModalOpen, 
        onClose: onCreateModalClose 
    } = useDisclosure();
    
    const { 
        isOpen: isDetailsModalOpen, 
        onOpen: onDetailsModalOpen, 
        onClose: onDetailsModalClose 
    } = useDisclosure();
    
    // Load initial data
    useEffect(() => {
        loadInitialData();
    }, []);

    const isAdminuser = () => {
        return userProfile && 
               (userProfile.role === 'admin' || userProfile.role === 'devops');
    };
    
    // Load calendar events when date range changes
    useEffect(() => {
        if (applications.length > 0) {
            loadCalendarEvents();
        }
    }, [calendarDate, calendarView, applications]);
    
    // Apply filters when they change or when allEvents changes
    useEffect(() => {
        applyFilters();
    }, [filters, allEvents]);
    
    const loadInitialData = async () => {
        try {
            setLoading(true);
            const clientIdParam = searchParams.get('clientId');
            if (clientIdParam) {
                setFilters(prev => ({
                    ...prev,
                    clientId: clientIdParam,
                    isCompleted: false
                }));
            }
            await Promise.all([
                loadApplications(),
                loadClients(),
                loadStatistics()
            ]);
            
        } catch (error) {
            console.error('Error loading initial data:', error);
            ToastManager.showError('Failed to load event scheduling data');
        } finally {
            setLoading(false);
        }
    };
    
    const loadApplications = async () => {
        try {
            const response = await apiService.fetchClientApplications();
            
            if (response?.success && response?.data) {
                const formattedApplications = response.data.map(app => ({
                    id: parseInt(app.appId),
                    appName: app.appName || app.name,
                    appDescription: app.appDescription || app.description,
                    appType: app.appType || app.type,
                    activeStatus: app.activeStatus,
                    version: app.version
                }));
                
                setApplications(formattedApplications);
            } else {
                setApplications([]);
            }
        } catch (error) {
            console.error('Error loading applications:', error);
            setApplications([]);
            ToastManager.showError('Failed to load applications');
        }
    };
    
    const loadClients = async () => {
        try {
            let response = await apiService.getAvailableClients();
            
            if (!response?.success || !response?.data) {
                response = await apiService.getClients();
            }
            
            if (response?.success && response?.data) {
                const clientsData = Array.isArray(response.data) ? response.data : response.data.content || [];
                const formattedClients = clientsData.map(client => ({
                    id: client.id || client.clientId,
                    name: client.name || client.clientName || client.displayName,
                    status: client.status
                }));
                
                setClients(formattedClients);
            } else {
                setClients([]);
            }
        } catch (error) {
            console.error('Error loading clients:', error);
            setClients([]);
        }
    };
    
    const loadStatistics = async () => {
        try {
            const response = await apiService.getEventStatistics();
            if (response?.success && response?.data) {
                setStatistics(response.data || {});
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
        }
    };
    
    const loadCalendarEvents = async () => {
        try {
            // Calculate date range based on current view
            const { start, end } = getCalendarDateRange(calendarDate, calendarView);
            
            const response = await apiService.getCalendarEvents(
                start.toISOString(),
                end.toISOString()
            );
            
            if (response?.success && response?.data) {
                setAllEvents(response.data);
            } else {
                setAllEvents([]);
            }
            
        } catch (error) {
            console.error('Error loading calendar events:', error);
            setAllEvents([]);
        }
    };
    
    const applyFilters = () => {
        let filteredEvents = [...allEvents];
        
        if (filters.applicationId && filters.applicationId !== '') {
            const applicationId = parseInt(filters.applicationId);
            filteredEvents = filteredEvents.filter(event => 
                parseInt(event.applicationId) === applicationId
            );
        }
        
        if (filters.clientId && filters.clientId !== '') {
            const clientId = parseInt(filters.clientId);
            filteredEvents = filteredEvents.filter(event => 
                event.clientDetails && event.clientDetails.some(client => 
                    parseInt(client.clientId) === clientId
                )
            );
        }
        
        if (filters.eventType && filters.eventType !== '') {
            filteredEvents = filteredEvents.filter(event => 
                event.eventType === filters.eventType
            );
        }
        
        if (filters.isCompleted !== '') {
            const isCompleted = filters.isCompleted === 'true';
            filteredEvents = filteredEvents.filter(event => 
                Boolean(event.isCompleted) === isCompleted
            );
        }
        
        if (filters.search && filters.search.trim() !== '') {
            const searchTerm = filters.search.toLowerCase().trim();
            filteredEvents = filteredEvents.filter(event => 
                event.eventTitle.toLowerCase().includes(searchTerm) ||
                event.eventDescription?.toLowerCase().includes(searchTerm) ||
                event.eventType.toLowerCase().includes(searchTerm) ||
                event.applicationName?.toLowerCase().includes(searchTerm) ||
                (event.clientDetails && event.clientDetails.some(client =>
                    client.clientName?.toLowerCase().includes(searchTerm)
                ))
            );
        }
        
        const formattedEvents = formatEventsForCalendar(filteredEvents);
        setEvents(formattedEvents);
    };
    
    const formatEventsForCalendar = (eventsData) => {
        return eventsData.map(event => {
            const clientNames = event.clientDetails 
                ? event.clientDetails.map(client => client.clientName).join(', ')
                : '';
            
            return {
                id: event.id,
                title: event.eventTitle,
                start: new Date(event.startDatetimeUtc),
                end: new Date(event.endDatetimeUtc),
                resource: {
                    ...event,
                    clientNames,
                    color: getEventColor(event.eventType, event.isCompleted),
                    textColor: getEventTextColor(event.eventType, event.isCompleted)
                }
            };
        });
    };
    
    const getEventColor = (eventType, isCompleted) => {
        if (isCompleted) return '#38A169';
        
        switch (eventType) {
            case 'Deployment':
                return '#3182CE';
            case 'Upgrade':
                return '#D69E2E';
            case 'Maintenance':
                return '#E53E3E';
            default:
                return '#718096';
        }
    };
    
    const getEventTextColor = (eventType, isCompleted) => {
        return '#FFFFFF';
    };
    
    const getCalendarDateRange = (date, view) => {
        const momentDate = moment(date);
        
        switch (view) {
            case 'month':
                return {
                    start: momentDate.clone().startOf('month').startOf('week'),
                    end: momentDate.clone().endOf('month').endOf('week')
                };
            case 'week':
                return {
                    start: momentDate.clone().startOf('week'),
                    end: momentDate.clone().endOf('week')
                };
            case 'day':
                return {
                    start: momentDate.clone().startOf('day'),
                    end: momentDate.clone().endOf('day')
                };
            default:
                return {
                    start: momentDate.clone().startOf('month'),
                    end: momentDate.clone().endOf('month')
                };
        }
    };
    
    // Calendar event handlers
    const handleSelectEvent = useCallback((event) => {
        setSelectedEvent(event.resource);
        onDetailsModalOpen();
    }, [onDetailsModalOpen]);
    
    const handleSelectSlot = useCallback((slotInfo) => {
        if (!isAdminuser()) {
            ToastManager.showWarning('Only admin users/ Devops can schedule events');
            return;
        }
        setSelectedEvent({
            startDatetimeUtc: slotInfo.start.toISOString(),
            endDatetimeUtc: slotInfo.end.toISOString()
        });
        onCreateModalOpen();
    }, [onCreateModalOpen]);
    
    const handleNavigate = useCallback((newDate) => {
        setCalendarDate(newDate);
    }, []);
    
    const handleViewChange = useCallback((newView) => {
        setCalendarView(newView);
    }, []);
    
    // Event operations
    const handleEventCreated = useCallback(() => {
        onCreateModalClose();
        setSelectedEvent(null);
        loadCalendarEvents();
        loadStatistics();
        ToastManager.showSuccess('Event scheduled successfully');
    }, [onCreateModalClose]);
    
    const handleEventUpdated = useCallback(() => {
        onDetailsModalClose();
        setSelectedEvent(null);
        loadCalendarEvents();
        loadStatistics();
        ToastManager.showSuccess('Event updated successfully');
    }, [onDetailsModalClose]);
    
    const handleEventDeleted = useCallback(() => {
        onDetailsModalClose();
        setSelectedEvent(null);
        loadCalendarEvents();
        loadStatistics();
        ToastManager.showSuccess('Event deleted successfully');
    }, [onDetailsModalClose]);
    
    const handleEventCompleted = useCallback(async (eventId) => {
        try {
            const response = await apiService.markEventAsCompleted(eventId);
            if (response?.success) {
                loadCalendarEvents();
                loadStatistics();
                ToastManager.showSuccess('Event marked as completed');
            } else {
                ToastManager.showError(response?.message || 'Failed to mark event as completed');
            }
        } catch (error) {
            console.error('Error marking event as completed:', error);
            ToastManager.showError('Failed to mark event as completed');
        }
    }, []);
    
    // Filter handlers
    const handleFilterChange = useCallback((field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);
    
    const clearFilters = useCallback(() => {
        setFilters({
            applicationId: '',
            clientId: '',
            eventType: '',
            isCompleted: '',
            search: ''
        });
    }, []);
    
    const refreshData = useCallback(() => {
        loadInitialData();
    }, []);
    
    const toggleFilters = useCallback(() => {
        setShowFilters(prev => !prev);
    }, []);
    
    const eventStyleGetter = useCallback((event) => {
        return {
            style: {
                backgroundColor: event.resource.color,
                color: event.resource.textColor,
                borderRadius: '4px',
                border: 'none',
                fontSize: '12px',
                padding: '2px 4px'
            }
        };
    }, []);
    
    const CustomEvent = ({ event }) => {
        const clientList = event.resource.clientDetails || [];
        
        return (
            <Tooltip
                label={
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Affected Clients:</Text>
                        {clientList.length > 0 ? (
                            <VStack align="start" spacing={0}>
                                {clientList.map((client, index) => (
                                    <Text key={index} fontSize="sm">
                                        • {client.clientName}
                                    </Text>
                                ))}
                            </VStack>
                        ) : (
                            <Text fontSize="sm">• None</Text>
                        )}
                    </Box>
                }
                hasArrow
                placement="top"
            >
                <Box
                    style={{
                        height: '100%',
                        width: '100%',
                        padding: '2px 4px',
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {event.title}
                </Box>
            </Tooltip>
        );
    };
    
    // Statistics cards
    const statisticsCards = useMemo(() => [
        {
            label: 'Total Events',
            value: statistics.totalEvents || 0,
            helpText: 'All scheduled events',
            color: 'blue'
        },
        {
            label: 'Upcoming Events',
            value: statistics.upcomingEvents || 0,
            helpText: 'Next 7 days',
            color: 'purple'
        },
        {
            label: 'Completed Events',
            value: statistics.completedEvents || 0,
            helpText: 'Successfully completed',
            color: 'green'
        },
        {
            label: 'Pending Events',
            value: statistics.pendingEvents || 0,
            helpText: 'Awaiting completion',
            color: 'orange'
        }
    ], [statistics]);
    
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
                <VStack spacing={4}>
                    <Spinner size="xl" color="blue.500" />
                    <Text>Loading event scheduling...</Text>
                </VStack>
            </Box>
        );
    }
    
    return (
        <Box p={6} bg={bgMain} minH="100vh">
            <VStack spacing={6} align="stretch">
                {/* Header Section */}
                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                    <VStack align="start" spacing={1}>
                        <Heading size="lg" color={headingColor}>
                            Event Scheduling
                        </Heading>
                        <Text color={textSecondary}>
                            Schedule and manage deployment, upgrade, and maintenance events
                        </Text>
                    </VStack>
                    
                    <HStack spacing={3}>
                        <Tooltip label="Refresh calendar">
                            <IconButton
                                icon={<MdRefresh />}
                                onClick={refreshData}
                                variant="outline"
                                size="sm"
                            />
                        </Tooltip>
                        
                        <Tooltip label={showFilters ? "Hide filters" : "Show filters"}>
                            <IconButton
                                icon={showFilters ? <MdVisibilityOff /> : <MdVisibility />}
                                onClick={toggleFilters}
                                variant="outline"
                                size="sm"
                            />
                        </Tooltip>
                        
                        {isAdminuser() && (<Button
                            leftIcon={<MdAdd />}
                            colorScheme="blue"
                            onClick={onCreateModalOpen}
                            size="sm"
                        >
                            Schedule Event
                        </Button>)}
                    </HStack>
                </Flex>
                
                {/* Statistics Cards */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                    {statisticsCards.map((stat, index) => (
                        <Card key={index} bg={cardBg} borderColor={borderColor}>
                            <CardBody>
                                <Stat size="sm">
                                    <StatLabel color={textSecondary}>
                                        {stat.label}
                                    </StatLabel>
                                    <StatNumber color={`${stat.color}.500`} fontSize="2xl">
                                        {stat.value}
                                    </StatNumber>
                                    <StatHelpText color={textTertiary}>
                                        {stat.helpText}
                                    </StatHelpText>
                                </Stat>
                            </CardBody>
                        </Card>
                    ))}
                </SimpleGrid>
                
                {/* Filters Section */}
                {showFilters && (
                    <Card bg={cardBg} borderColor={borderColor}>
                        <CardBody>
                        <VStack spacing={4} align="stretch">
                            <HStack spacing={4} wrap="wrap">
                                <InputGroup maxW="300px">
                                    <InputLeftElement>
                                        <MdSearch color="gray.400" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search events..."
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                    />
                                </InputGroup>
                                
                                <Select
                                    placeholder="All Applications"
                                    value={filters.applicationId}
                                    onChange={(e) => handleFilterChange('applicationId', e.target.value)}
                                    maxW="200px"
                                >
                                    {applications.map(app => (
                                        <option key={app.id} value={app.id}>
                                            {app.appName}
                                        </option>
                                    ))}
                                </Select>
                                
                                <Select
                                    placeholder="All Clients"
                                    value={filters.clientId}
                                    onChange={(e) => handleFilterChange('clientId', e.target.value)}
                                    maxW="200px"
                                >
                                    {clients.map(client => (
                                        <option key={client.id} value={client.id}>
                                            {client.name}
                                        </option>
                                    ))}
                                </Select>
                                
                                <Select
                                    placeholder="All Types"
                                    value={filters.eventType}
                                    onChange={(e) => handleFilterChange('eventType', e.target.value)}
                                    maxW="150px"
                                >
                                    <option value="Deployment">Deployment</option>
                                    <option value="Upgrade">Upgrade</option>
                                    <option value="Maintenance">Maintenance</option>
                                </Select>
                                
                                <Select
                                    placeholder="All Status"
                                    value={filters.isCompleted}
                                    onChange={(e) => handleFilterChange('isCompleted', e.target.value)}
                                    maxW="150px"
                                >
                                    <option value="false">Pending</option>
                                    <option value="true">Completed</option>
                                </Select>
                                
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={clearFilters}
                                    leftIcon={<MdFilterList />}
                                >
                                    Clear Filters
                                </Button>
                            </HStack>
                            
                            {(filters.search || filters.applicationId || filters.clientId || filters.eventType || filters.isCompleted !== '') && (
                                <HStack spacing={2} flexWrap="wrap">
                                    <Text fontSize="sm" color={textSecondary}>Active filters:</Text>
                                    {filters.search && (
                                        <Badge colorScheme="blue" variant="subtle">
                                            Search: "{filters.search}"
                                        </Badge>
                                    )}
                                    {filters.applicationId && (
                                        <Badge colorScheme="green" variant="subtle">
                                            App: {applications.find(a => a.id === parseInt(filters.applicationId))?.appName || filters.applicationId}
                                        </Badge>
                                    )}
                                    {filters.clientId && (
                                        <Badge colorScheme="teal" variant="subtle">
                                            Client: {clients.find(c => c.id === parseInt(filters.clientId))?.name || filters.clientId}
                                        </Badge>
                                    )}
                                    {filters.eventType && (
                                        <Badge colorScheme="purple" variant="subtle">
                                            Type: {filters.eventType}
                                        </Badge>
                                    )}
                                    {filters.isCompleted !== '' && (
                                        <Badge colorScheme="orange" variant="subtle">
                                            Status: {filters.isCompleted === 'true' ? 'Completed' : 'Pending'}
                                        </Badge>
                                    )}
                                    <Text fontSize="sm" color={textSecondary}>
                                        ({events.length} events shown)
                                    </Text>
                                </HStack>
                            )}
                        </VStack>
                    </CardBody>
                </Card>
                )}
                
                {/* Calendar Section */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardBody>
                        <Box height="600px">
                            <Calendar
                                localizer={localizer}
                                events={events}
                                startAccessor="start"
                                endAccessor="end"
                                style={{ height: '100%' }}
                                onSelectEvent={handleSelectEvent}
                                onSelectSlot={handleSelectSlot}
                                selectable
                                eventPropGetter={eventStyleGetter}
                                components={{
                                    event: CustomEvent
                                }}
                                onNavigate={handleNavigate}
                                onView={handleViewChange}
                                view={calendarView}
                                date={calendarDate}
                                popup={true}
                                popupOffset={30}
                                views={['month', 'week', 'day']}
                                messages={{
                                    next: 'Next',
                                    previous: 'Previous',
                                    today: 'Today',
                                    month: 'Month',
                                    week: 'Week',
                                    day: 'Day'
                                }}
                                formats={{
                                    timeGutterFormat: 'HH:mm',
                                    eventTimeRangeFormat: ({ start, end }) =>
                                        `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`
                                }}
                            />
                        </Box>
                    </CardBody>
                </Card>
                
                {/* Event Legend */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardBody>
                        <Text fontWeight="semibold" mb={3}>Event Types:</Text>
                        <HStack spacing={6} wrap="wrap">
                            <HStack>
                                <Box w={4} h={4} bg="blue.500" borderRadius="sm" />
                                <Text fontSize="sm">Deployment</Text>
                            </HStack>
                            <HStack>
                                <Box w={4} h={4} bg="yellow.500" borderRadius="sm" />
                                <Text fontSize="sm">Upgrade</Text>
                            </HStack>
                            <HStack>
                                <Box w={4} h={4} bg="red.500" borderRadius="sm" />
                                <Text fontSize="sm">Maintenance</Text>
                            </HStack>
                            <HStack>
                                <Box w={4} h={4} bg="green.500" borderRadius="sm" />
                                <Text fontSize="sm">Completed</Text>
                            </HStack>
                        </HStack>
                    </CardBody>
                </Card>
            </VStack>
            
            {/* Event Creation Modal */}
            <EventScheduleModal
                isOpen={isCreateModalOpen}
                onClose={() => {
                    onCreateModalClose();
                    setSelectedEvent(null);
                }}
                onEventCreated={handleEventCreated}
                applications={applications}
                initialData={selectedEvent}
                userProfile={userProfile}
            />
            
            {/* Event Details Modal */}
            <EventDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={() => {
                    onDetailsModalClose();
                    setSelectedEvent(null);
                }}
                event={selectedEvent}
                onEventUpdated={handleEventUpdated}
                onEventDeleted={handleEventDeleted}
                onEventCompleted={handleEventCompleted}
                userProfile={userProfile}
            />
        </Box>
    );
}

export default EventScheduling;